import React from 'react';

import { LinearProgress } from '@material-ui/core';
import styled, { css } from 'styled-components';
import { VerticallyAlignedFlexContainer } from 'styles/common';

import theme from 'constants/theme';

const PROGRESS_KEYS = {
  BUILDING: 'BUILDING',
  EXCELLED: 'EXCELLED',
  NOT_CRITICAL: 'NOT_CRITICAL',
  SKILLED: 'SKILLED',
  STARTING: 'STARTING',
};

export const getProgressBarColor = (status) => {
  switch (status) {
    case PROGRESS_KEYS.BUILDING:
      return theme.palette.lightOrange.main;
    case PROGRESS_KEYS.EXCELLED:
      return theme.palette.icons.blue;
    case PROGRESS_KEYS.NOT_CRITICAL:
      return theme.palette.icons.grey;
    case PROGRESS_KEYS.SKILLED:
      return theme.palette.icons.green;
    case PROGRESS_KEYS.STARTING:
      return theme.palette.purple.darkv1;
    default:
      return '';
  }
};

const CompetencyProgressBar = ({
  filledBlocks = 0,
  totalBlocks = 0,
  progress = null,
  barHeight = '0.875rem',
  percentage = 0,
}) => {
  const percentageOfOneBlock = 100 / totalBlocks;
  const lastFilledBlockedPercentage = percentage
    ? (percentage - percentageOfOneBlock * (filledBlocks - 1)) * totalBlocks
    : 0;
  const actualFilledBlock = Math.floor(percentage / percentageOfOneBlock);

  const blocksAreCompletelyFilled = !(percentage % percentageOfOneBlock); // used to determine if filled blocks are completely filled

  return (
    <VerticallyAlignedFlexContainer style={{ borderRadius: '20px' }}>
      {new Array(totalBlocks).fill('progressBar').map((_, index) => {
        if (index <= actualFilledBlock - 1) {
          return (
            <StyledLinearProgress
              variant="determinate"
              value={100}
              $barColor={getProgressBarColor(progress)}
              $barHeight={barHeight}
              $isFirstBlock={index === 0}
              $isLastBlock={index === totalBlocks - 1}
              key={`progress-bar-${index}-filled`}
            />
          );
        }
        if (index === actualFilledBlock && !blocksAreCompletelyFilled) {
          // if it's the next block to the last filled block
          return (
            <StyledLinearProgress
              variant="determinate"
              value={lastFilledBlockedPercentage}
              $barColor={getProgressBarColor(progress)}
              $bgColor={theme.palette.background.lighterGrey}
              $barHeight={barHeight}
              $isFirstBlock={index === 0}
              $isLastBlock={index === totalBlocks - 1}
              key={`progress-bar-percentage-${percentage}-${index}-filled`}
            />
          );
        }
        return (
          <StyledLinearProgress
            variant="determinate"
            value={100}
            $barColor={theme.palette.background.lighterGrey}
            $barHeight={barHeight}
            $isFirstBlock={index === 0}
            $isLastBlock={index === totalBlocks - 1}
            key={`progress-bar-${index}-non-filled`}
          />
        );
      })}
    </VerticallyAlignedFlexContainer>
  );
};

const StyledLinearProgress = styled(LinearProgress)`
  ${(props) =>
    props.$bgColor &&
    css`
      background: ${(props) => props.$bgColor};
    `}

  .MuiLinearProgress-bar1Determinate {
    ${(props) =>
      props.$barColor &&
      css`
        background: ${(props) => props.$barColor};
      `}
  }
  &.MuiLinearProgress-root {
    display: inline-block;
    width: 100%;
    margin-right: 0.25rem;
    height: ${(props) => props.$barHeight};
    ${(props) =>
      props.$isLastBlock &&
      css`
        border-top-right-radius: inherit;
        border-bottom-right-radius: inherit;
      `}
    ${(props) =>
      props.$isFirstBlock &&
      css`
        border-top-left-radius: inherit;
        border-bottom-left-radius: inherit;
      `}
  }
`;

export default CompetencyProgressBar;
